import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle, Calendar } from "lucide-react";
import { SITE, whatsappLink, openWhatsApp } from "@/lib/site";
import CalendlyInline from "@/components/CalendlyInline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_OPTIONS = [
  "Morning (9–12)",
  "Afternoon (12–4)",
  "Evening (4–7)",
  "Anytime",
];

export default function ConsultationSection() {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    phone: "",
    best_time_to_call: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads/consultation`, form);
      toast.success(res.data?.message || "Consultation request received.");
      setForm({ first_name: "", email: "", phone: "", best_time_to_call: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="consultation-section"
      className="relative py-24 md:py-40 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 right-0 w-[55vw] h-[55vw] ambient-radial pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
        {/* LEFT — copy + WhatsApp */}
        <div className="reveal">
          <div className="text-[11px] uppercase tracking-[0.28em] text-rolex-light mb-6">
            Strategy Session
          </div>
          <h2 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
            Book your{" "}
            <span className="italic text-rolex-light">free</span> strategy
            session.
          </h2>
          <p className="mt-8 text-lg text-gray-400 max-w-xl leading-relaxed font-light">
            Speak directly with a licensed professional about protection,
            wealth-building, and financial strategies.
          </p>

          <div className="mt-12 rounded-3xl glass p-8 max-w-xl">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-rolex/10 border border-rolex/30">
                <MessageCircle className="h-5 w-5 text-rolex-light" />
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-400">Prefer to chat instantly?</div>
                <a
                  href={whatsappLink("Hi! I'd like to book a strategy session.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    openWhatsApp("Hi! I'd like to book a strategy session.");
                  }}
                  data-testid="consultation-whatsapp-cta"
                  className="mt-3 inline-flex items-center gap-2 text-white hover:text-rolex-light transition-colors font-medium"
                >
                  Message us on WhatsApp
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 rounded-3xl glass p-8 max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-5 w-5 text-rolex-light" />
              <span className="text-sm uppercase tracking-[0.22em] text-gray-300">
                Pick a time on Calendly
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-5 font-light leading-relaxed">
              Skip the form — scroll down to our scheduler and choose any slot
              that fits your week.
            </p>
            <a
              href="#calendly-embed"
              data-testid="consultation-calendly-cta"
              className="inline-flex items-center gap-2 text-rolex-light hover:text-white transition-colors text-sm font-medium uppercase tracking-[0.22em]"
            >
              Jump to scheduler
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* RIGHT — form */}
        <form
          onSubmit={onSubmit}
          data-testid="consultation-form"
          className="reveal relative rounded-3xl glass p-8 md:p-12 border-white/10"
          style={{ transitionDelay: "120ms" }}
        >
          <div
            aria-hidden
            className="absolute -inset-px rounded-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(120% 80% at 50% 0%, rgba(0,98,65,0.15), rgba(0,0,0,0) 60%)",
            }}
          />
          <div className="relative space-y-6">
            <Field
              label="First Name"
              testid="consultation-input-first-name"
              value={form.first_name}
              onChange={update("first_name")}
              placeholder="Sarah"
            />
            <Field
              label="Email"
              type="email"
              testid="consultation-input-email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@email.com"
            />
            <Field
              label="Phone Number"
              type="tel"
              testid="consultation-input-phone"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+1 (555) 000-0000"
            />

            <div>
              <label className="block text-[11px] uppercase tracking-[0.28em] text-gray-500 mb-3">
                Best Time To Call
              </label>
              <div className="grid grid-cols-2 gap-2">
                {TIME_OPTIONS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() =>
                      setForm({ ...form, best_time_to_call: t })
                    }
                    data-testid={`consultation-time-${t.split(" ")[0].toLowerCase()}`}
                    className={`px-4 py-3 rounded-full text-sm transition-all border ${
                      form.best_time_to_call === t
                        ? "bg-rolex/15 border-rolex text-white"
                        : "bg-white/[0.02] border-white/10 text-gray-400 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="consultation-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#006241] hover:bg-[#0a8a5e] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full text-[15px] font-medium transition-all duration-300 shadow-[0_0_40px_rgba(0,98,65,0.35)] hover:shadow-[0_0_60px_rgba(0,98,65,0.55)]"
            >
              {submitting ? "Submitting..." : "Book My Consultation"}
              <ArrowUpRight className="h-4 w-4" />
            </button>

            <p className="text-xs text-gray-600 leading-relaxed font-light">
              We respect your privacy. Your information is used only to schedule
              your strategy session.
            </p>
          </div>
        </form>
      </div>

      {/* Inline Calendly scheduler — full-width below the two-column row */}
      <div
        id="calendly-embed"
        className="relative max-w-5xl mx-auto px-6 md:px-12 mt-24 scroll-mt-28 reveal"
      >
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-rolex-light mb-4">
            Book a 30-minute call
          </div>
          <h3 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight">
            Choose a time that works for you.
          </h3>
          <p className="mt-5 text-gray-400 text-sm md:text-base font-light max-w-xl mx-auto">
            Tap <span className="text-white font-medium">"30 Minute Meeting"</span> below
            to pick a slot. You'll get an instant confirmation email.
          </p>
        </div>
        <CalendlyInline height={760} />
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.28em] text-gray-500 mb-3">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.03] border border-white/10 focus:border-rolex focus:bg-white/[0.05] focus:outline-none rounded-2xl px-5 py-4 text-white placeholder:text-gray-600 transition-all duration-300"
      />
    </div>
  );
}
